import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface HeroSlide {
  id: number;
  image: string;
  imageMobile?: string;
  title?: string;
  subtitle?: string;
  ctaText?: string;
  ctaSecondary?: string;
  objectPosition?: string;
}

interface HeroSliderProps {
  slides: HeroSlide[];
}

export const HeroSlider: React.FC<HeroSliderProps> = ({ slides }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Auto-advance slides
  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000); // 5 seconds per slide

    return () => clearInterval(interval);
  }, [isAutoPlaying, slides.length]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    // Resume auto-play after 10 seconds
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const goToPrevious = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  return (
    <div className="relative h-[70vh] max-h-[800px] min-h-[500px] overflow-hidden">
      {/* Slides */}
      <div className="relative w-full h-full">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {/* Background Image */}
            <div className="absolute inset-0">
              <picture>
                <source srcSet={`${slide.image}?w=1920&q=80`} media="(min-width: 1024px)" />
                <source srcSet={`${slide.image}?w=1280&q=80`} media="(min-width: 768px)" />
                <source srcSet={`${(slide.imageMobile ?? slide.image)}?w=800&q=80`} media="(min-width: 480px)" />
                <img
                  src={`${(slide.imageMobile ?? slide.image)}?w=480&q=80`}
                  alt={slide.title ?? 'hero slide'}
                  className="w-full h-full object-cover"
                  style={{ objectPosition: slide.objectPosition || 'center' }}
                  loading={index === 0 ? 'eager' : 'lazy'}
                  decoding="async"
                />
              </picture>
              <div className="absolute inset-0 bg-black/40 md:bg-black/30"></div>
              <div className="absolute inset-0 bg-gradient-to-r from-primary-900/70 to-primary-700/50 md:from-primary-900/60 md:to-primary-700/40"></div>
            </div>

            {/* Content (optional) */}
            {(slide.title || slide.subtitle) && (
              <div className="relative z-10 flex items-center h-full">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-12">
                  <div className="max-w-2xl md:max-w-3xl lg:max-w-3xl text-white px-4 sm:px-6">
                    {slide.title && (
                      <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl xl:text-6xl font-bold leading-tight mb-4 md:mb-6">
                        {slide.title}
                      </h1>
                    )}
                    {slide.subtitle && (
                      <p className="text-base sm:text-lg text-gray-100 mb-6 md:mb-8 max-w-2xl leading-relaxed">
                        {slide.subtitle}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={goToPrevious}
        className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 w-12 h-12 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center transition-all duration-fast"
        aria-label="Previous slide"
      >
        <ChevronLeft className="w-6 h-6 text-white" />
      </button>
      <button
        onClick={goToNext}
        className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 w-12 h-12 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center transition-all duration-fast"
        aria-label="Next slide"
      >
        <ChevronRight className="w-6 h-6 text-white" />
      </button>

      {/* Dots Indicator */}
      <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-20 flex space-x-3 bg-black bg-opacity-20 px-4 py-2 rounded-full">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-3 h-3 rounded-full transition-all duration-fast ${
              index === currentSlide
                ? 'bg-white scale-110'
                : 'bg-white bg-opacity-60 hover:bg-opacity-90'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Progress Bar */}
      <div className="absolute bottom-0 left-0 w-full h-1 bg-white bg-opacity-20 z-20">
        <div
          className="h-full bg-white transition-all duration-100 ease-linear"
          style={{
            width: isAutoPlaying ? `${((currentSlide + 1) / slides.length) * 100}%` : '0%',
          }}
        />
      </div>
    </div>
  );
};
